import React from 'react';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { History, Circle, Sparkles, ArrowRight } from 'lucide-react';

interface TimelineStripProps {
  hasRecentSanctuaryEntry: boolean;
  pastNote?: string;
  presentNote?: string;
  futureNote?: string;
}

export const TimelineStrip: React.FC<TimelineStripProps> = ({
  hasRecentSanctuaryEntry,
  pastNote,
  presentNote,
  futureNote
}) => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-light text-foreground tracking-wide">Your Timeline</h2>
      </div>
      
      <div className="relative">
        {/* Connecting line */}
        <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-muted/30 via-primary/20 to-muted/30 -translate-y-1/2 hidden sm:block" />
        
        <div className="grid grid-cols-3 gap-3 sm:gap-6">
          {/* Past */}
          <Card 
            className="group relative border-0 bg-card/40 backdrop-blur-sm hover:bg-card/70 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            onClick={() => navigate('/sanctuary')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-muted/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="p-4 sm:p-6 flex flex-col items-center text-center space-y-3 relative">
              <div className="w-12 h-12 rounded-full bg-muted/20 flex items-center justify-center group-hover:bg-muted/30 transition-colors">
                <History className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <div className="text-xs font-light text-muted-foreground tracking-widest uppercase">Past</div>
                <div className="text-sm text-foreground font-light">
                  {pastNote || 'Sanctuary'}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Card>

          {/* Present - Highlighted */}
          <Card 
            className="group relative border-0 bg-gradient-to-br from-primary/15 to-accent/10 shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            onClick={() => navigate('/daily-check-in')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="p-4 sm:p-6 flex flex-col items-center text-center space-y-3 relative">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors ring-2 ring-primary/30">
                <Circle className="w-5 h-5 text-primary fill-primary/20" />
              </div>
              <div className="space-y-1">
                <div className="text-xs font-light text-primary tracking-widest uppercase">Present</div>
                <div className="text-sm text-foreground font-light">
                  {presentNote || 'Check-In'}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Card>

          {/* Future */}
          <Card 
            className="group relative border-0 bg-card/40 backdrop-blur-sm hover:bg-card/70 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
            onClick={() => navigate('/future-self-navigator')}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-accent/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
            <div className="p-4 sm:p-6 flex flex-col items-center text-center space-y-3 relative">
              <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                <Sparkles className="w-5 h-5 text-accent" />
              </div>
              <div className="space-y-1">
                <div className="text-xs font-light text-muted-foreground tracking-widest uppercase">Future</div>
                <div className="text-sm text-foreground font-light">
                  {futureNote || 'Future Self'}
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};